import React from 'react'
import useSWR from 'swr'

import { getAllGptConversations } from '../../api/gpt'
import { AdvisorMessage } from '../../api/gpt/types'

// Constants for sessionStorage keys
const CURRENT_CONVO_ID = 'advisor_current_convo_id'

const CACHE_KEYS = {
  BASE: 'gpt',
  ALL: 'all',
} as const

export const useAllConversations = () => {
  return useSWR([CACHE_KEYS.BASE, CACHE_KEYS.ALL], () => getAllGptConversations())
}

export const useConversation = () => {
  const { data: allConversations } = useAllConversations()

  const getCurrentConversationId = (): number | null => {
    const currentConvoId = sessionStorage.getItem(CURRENT_CONVO_ID)
    return currentConvoId ? parseInt(currentConvoId) : null
  }

  const setCurrentConversationId = (id: number): void => {
    sessionStorage.setItem(CURRENT_CONVO_ID, id.toString())
    setConversationId(id)
  }

  // State for conversation
  const [conversationId, setConversationId] = React.useState<number | null>(getCurrentConversationId())
  const [messages, setMessages] = React.useState<AdvisorMessage[]>([])

  const handleConversationId = (event: CustomEvent<{conversationId: number}>) => {
    setCurrentConversationId(event.detail.conversationId)
  }

  // Setup event listener for conversation ID
  React.useEffect(() => {
    window.addEventListener('conversation-id-received', handleConversationId as EventListener)

    return () => {
      window.removeEventListener('conversation-id-received', handleConversationId as EventListener)
    }
  }, [])

  React.useEffect(() => {
    if (conversationId && allConversations && allConversations?.length > 0) {
      const currentConversation = allConversations.find(conversation => conversation.id === conversationId)?.messages
      if(currentConversation) {
        setMessages(currentConversation)
      }
    }
  }, [allConversations, conversationId])

  // Method to add a message to conversation
  const addMessage = (message: AdvisorMessage, currentMessages: AdvisorMessage[] = messages): AdvisorMessage[] => {
    const newMessages = [message, ...currentMessages]
    setMessages(newMessages)
    return newMessages
  }

  // Method to clear conversation
  const clearConversation = () => {
    sessionStorage.removeItem(CURRENT_CONVO_ID)
    setConversationId(null)
    setMessages([])
  }

  return {
    messages,
    conversationId,
    setCurrentConversationId,
    addMessage,
    clearConversation
  }
}
